/**
 * HealingVisualEngine — Reclaim Healing Visual System
 *
 * FINAL POLISH: Continuous, organic, fully embodied.
 *
 * Structure:
 *   - Subtle human silhouette for grounding
 *   - Continuous spinal energy line with integrated nodes
 *   - Smooth flowing light stream (not step-by-step jumps)
 *   - Nodes brighten as light flows through, glow softly overlaps
 *   - Base grounding/completion effect
 *   - Whole-field breath for aliveness
 *
 * Feeling: Effortless. Continuous. Safe. Alive.
 * No sharpness, no harsh transitions.
 *
 * "A repeatable healing visual language."
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const VERTEBRAE = [
  { y: 0.05, size: 0.7,  label: 'c1' },
  { y: 0.13, size: 0.75, label: 'c4' },
  { y: 0.21, size: 0.85, label: 't1' },
  { y: 0.30, size: 0.95, label: 't4' },
  { y: 0.39, size: 1.0,  label: 't7' },
  { y: 0.48, size: 1.05, label: 't10' },
  { y: 0.57, size: 1.1,  label: 'l1' },
  { y: 0.66, size: 1.05, label: 'l3' },
  { y: 0.75, size: 0.95, label: 'l5' },
  { y: 0.84, size: 0.85, label: 's1' },
  { y: 0.93, size: 0.7,  label: 's3' },
];

const HealingVisualEngine = ({ condition = 'spine', elapsed = 0, totalDuration = 420, isActive = false }) => {
  const [breath, setBreath] = useState(0.5);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  const progress = Math.min(1, elapsed / totalDuration);
  const voiceEnd = 102 / totalDuration;

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();
    const tick = () => {
      const t = (Date.now() - startRef.current) % 14000;
      setBreath(0.5 + 0.5 * Math.sin((t / 14000) * Math.PI * 2 - Math.PI / 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  // Smooth non-linear light position (0→1, top→bottom)
  const lightY = useMemo(() => {
    const t20 = 20 / totalDuration;
    const t50 = 50 / totalDuration;
    if (progress < t20) return (progress / t20) * 0.05;
    if (progress < t50) {
      const t = (progress - t20) / (t50 - t20);
      return 0.05 + t * t * 0.18;
    }
    if (progress < voiceEnd) {
      const t = (progress - t50) / (voiceEnd - t50);
      // Slow down at the base (last 20%)
      const eased = t < 0.8 ? t / 0.8 * 0.85 : 0.85 + ((t - 0.8) / 0.2) * 0.15;
      return 0.23 + eased * 0.77;
    }
    return 1;
  }, [progress, totalDuration, voiceEnd]);

  const phase = useMemo(() => {
    if (progress < 20 / totalDuration) return 'arrival';
    if (progress < 50 / totalDuration) return 'activation';
    if (progress < voiceEnd) return 'descent';
    return 'integration';
  }, [progress, totalDuration, voiceEnd]);

  const intensity = useMemo(() => {
    if (phase === 'arrival') return 0.45 + breath * 0.1;
    if (phase === 'activation') return 0.7 + breath * 0.12;
    if (phase === 'descent') return 0.88 + breath * 0.08;
    return 1.0;
  }, [phase, breath]);

  const spineTop = 8;
  const spineHeight = 76;

  // Smooth continuous activation — no hard steps
  const nodeGlow = (v) => {
    const dist = lightY - v.y;
    if (dist < -0.12) return 0;                       // too far ahead — dark
    if (dist < 0) return Math.pow((dist + 0.12) / 0.12, 2) * 0.6; // approaching — gentle fade in
    if (dist < 0.06) return 0.6 + (dist / 0.06) * 0.4;            // just passed — brightening
    return 1;                                                        // fully lit — settled
  };

  // Is the light currently near this node? (for bloom)
  const isNearLight = (v) => Math.abs(lightY - v.y) < 0.08;

  // How "grounded" is the base? (for completion effect)
  const baseGrounding = lightY > 0.85 ? Math.min(1, (lightY - 0.85) / 0.15) : 0;

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="healing-visual-engine">

      {/* Background — deep warm dark */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 120% 100% at 50% 45%, rgba(35,30,16,${0.25 + intensity * 0.3}) 0%, rgba(10,8,5,1) 55%)`,
      }} />

      {/* === 5. BREATH FIELD — very slow expansion/contraction of surrounding glow === */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          animate={{
            width: 160 + breath * 25,
            height: '78%',
            opacity: 0.06 + intensity * 0.08 + breath * 0.03,
          }}
          transition={{ duration: 2.5, ease: 'easeInOut' }}
        >
          <div className="w-full h-full rounded-[50%]" style={{
            background: 'radial-gradient(ellipse 100% 100% at 50% 46%, rgba(195,170,110,0.2) 0%, rgba(175,150,95,0.06) 40%, transparent 58%)',
            filter: 'blur(30px)',
          }} />
        </motion.div>
      </div>

      {/* === 4. HUMAN SILHOUETTE — +5% presence, soft and diffused === */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div
          animate={{
            opacity: 0.20 + intensity * 0.14 + breath * 0.02,
            scale: 1 + breath * 0.003,
          }}
          transition={{ duration: 2.5, ease: 'easeInOut' }}
        >
          <svg
            viewBox="0 0 200 900"
            style={{ width: '190px', height: '82vh', filter: 'blur(5px)' }}
            fill="none"
          >
            <defs>
              <linearGradient id="bodyFill" x1="50%" y1="0%" x2="50%" y2="100%">
                <stop offset="0%" stopColor="rgb(85,74,48)" stopOpacity="0.5" />
                <stop offset="25%" stopColor="rgb(68,60,38)" stopOpacity="0.6" />
                <stop offset="55%" stopColor="rgb(60,52,34)" stopOpacity="0.58" />
                <stop offset="100%" stopColor="rgb(68,60,38)" stopOpacity="0.48" />
              </linearGradient>
            </defs>
            <path
              d={`
                M 100,15
                C 116,15 126,28 126,40
                C 126,48 120,55 114,58
                C 128,64 158,80 170,108
                C 178,128 175,148 168,165
                C 164,200 160,260 158,330
                C 157,390 157,430 158,480
                C 160,515 165,548 170,580
                C 174,608 172,640 166,668
                C 158,692 148,715 135,740
                L 65,740
                C 52,715 42,692 34,668
                C 28,640 26,608 30,580
                C 35,548 40,515 42,480
                C 43,430 43,390 42,330
                C 40,260 36,200 32,165
                C 25,148 22,128 30,108
                C 42,80 72,64 86,58
                C 80,55 74,48 74,40
                C 74,28 84,15 100,15
                Z
              `}
              fill="url(#bodyFill)"
            />
            <path
              d={`
                M 100,15
                C 116,15 126,28 126,40
                C 126,48 120,55 114,58
                C 128,64 158,80 170,108
                C 178,128 175,148 168,165
                C 164,200 160,260 158,330
                C 157,390 157,430 158,480
                C 160,515 165,548 170,580
                C 174,608 172,640 166,668
                C 158,692 148,715 135,740
                L 65,740
                C 52,715 42,692 34,668
                C 28,640 26,608 30,580
                C 35,548 40,515 42,480
                C 43,430 43,390 42,330
                C 40,260 36,200 32,165
                C 25,148 22,128 30,108
                C 42,80 72,64 86,58
                C 80,55 74,48 74,40
                C 74,28 84,15 100,15
                Z
              `}
              fill="none"
              stroke="rgba(125,110,75,0.14)"
              strokeWidth="1.5"
            />
          </svg>
        </motion.div>
      </div>

      {condition === 'spine' && (
        <div className="absolute inset-0">

          {/* === 2. VERTICAL ENERGY LINE — connects all nodes into one living stream === */}
          <div
            className="absolute pointer-events-none"
            style={{
              left: '50%',
              top: `${spineTop + 0.03 * spineHeight}%`,
              transform: 'translateX(-50%)',
              width: 3 + breath * 1,
              height: `${0.92 * spineHeight}%`,
              opacity: intensity * 0.35,
              background: `linear-gradient(180deg, 
                transparent 0%,
                rgba(215,190,125,${Math.min(1, lightY / 0.1) * 0.25}) 5%,
                rgba(215,190,125,${Math.min(lightY, 1) * 0.2}) ${Math.min(100, lightY * 100)}%,
                rgba(215,190,125,0.04) ${Math.min(100, lightY * 100 + 8)}%,
                transparent ${Math.min(100, lightY * 100 + 15)}%)`,
              filter: 'blur(1.5px)',
              borderRadius: 2,
            }}
          />

          {/* === 1 + 2. VERTEBRA NODES — integrated, overlapping glow, continuous === */}
          {VERTEBRAE.map((v, i) => {
            const glow = nodeGlow(v);
            const near = isNearLight(v);

            // Smooth pulse when light is near
            const nearPulse = near ? (1 - Math.abs(lightY - v.y) / 0.08) * 0.5 : 0;

            // Node dimensions — allow overlap with neighbors
            const baseW = 14 + v.size * 16;
            const nodeW = baseW * (1 + glow * 0.35 + nearPulse * 0.2 + breath * 0.05);
            const nodeH = nodeW * (0.42 + glow * 0.1);

            // Is this in the lower spine? (for grounding expansion)
            const isBase = v.y > 0.8;
            const groundExpand = isBase ? baseGrounding * 0.4 : 0;

            return (
              <div key={v.label}>

                {/* NODE GLOW — soft warm disc, fades in smoothly as light approaches */}
                <motion.div
                  className="absolute pointer-events-none"
                  style={{
                    left: '50%',
                    top: `${spineTop + v.y * spineHeight}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  animate={{
                    width: nodeW * (1 + groundExpand),
                    height: nodeH * (1 + groundExpand * 0.6),
                    opacity: glow < 0.01
                      ? 0.1 + breath * 0.05   // Unlit — faint presence
                      : 0.12 + glow * 0.75 * intensity + nearPulse * 0.15,
                  }}
                  transition={{ duration: 1.8, ease: 'easeOut' }}
                >
                  <div className="w-full h-full rounded-full" style={{
                    background: glow > 0.1
                      ? `radial-gradient(ellipse 90% 80% at 50% 50%, 
                          rgba(255,232,160,${0.85 + nearPulse * 0.15}) 0%, 
                          rgba(245,218,140,${0.35 + glow * 0.15}) 35%, 
                          rgba(230,200,115,${0.08 + glow * 0.06}) 60%,
                          transparent 82%)`
                      : `radial-gradient(ellipse 90% 80% at 50% 50%, 
                          rgba(170,150,100,0.3) 0%, 
                          rgba(150,130,85,0.1) 40%, 
                          transparent 70%)`,
                    filter: `blur(${glow > 0.5 ? 0.5 : 1}px)`,
                  }} />
                </motion.div>

                {/* BRIGHT CORE — warm center of each activated node */}
                {glow > 0.3 && (
                  <motion.div
                    className="absolute pointer-events-none"
                    style={{
                      left: '50%',
                      top: `${spineTop + v.y * spineHeight}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                    animate={{
                      width: nodeW * 0.38,
                      height: nodeH * 0.5,
                      opacity: glow * (0.55 + nearPulse * 0.4) * intensity,
                    }}
                    transition={{ duration: 1.2, ease: 'easeOut' }}
                  >
                    <div className="w-full h-full rounded-full" style={{
                      background: 'radial-gradient(ellipse, rgba(255,240,185,0.95) 0%, rgba(250,228,165,0.5) 45%, transparent 78%)',
                    }} />
                  </motion.div>
                )}

                {/* BLOOM — soft expansion when light flows through this node */}
                {glow > 0.4 && (
                  <motion.div
                    className="absolute pointer-events-none"
                    style={{
                      left: '50%',
                      top: `${spineTop + v.y * spineHeight}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                    animate={{
                      width: nodeW * (2.2 + nearPulse * 0.8 + groundExpand * 2),
                      height: nodeH * (2.8 + nearPulse * 0.6 + groundExpand * 1.5),
                      opacity: (near ? 0.35 + nearPulse * 0.2 : glow * 0.18) * intensity,
                    }}
                    transition={{ duration: 2.5, ease: 'easeInOut' }}
                  >
                    <div className="w-full h-full rounded-full" style={{
                      background: 'radial-gradient(ellipse, rgba(240,218,145,0.3) 0%, rgba(230,205,125,0.08) 40%, transparent 62%)',
                      filter: 'blur(8px)',
                    }} />
                  </motion.div>
                )}

                {/* CONNECTING GLOW — faint bridge to next node (slight overlap) */}
                {VERTEBRAE[i + 1] && glow > 0.3 && (
                  <motion.div
                    className="absolute pointer-events-none"
                    style={{
                      left: '50%',
                      top: `${spineTop + v.y * spineHeight}%`,
                      transform: 'translateX(-50%)',
                    }}
                    animate={{
                      width: 6 + glow * 4 + breath * 2,
                      height: `${(VERTEBRAE[i + 1].y - v.y) * spineHeight + 1}%`,
                      opacity: glow * 0.2 * intensity,
                    }}
                    transition={{ duration: 2, ease: 'easeOut' }}
                  >
                    <div className="w-full h-full" style={{
                      background: `linear-gradient(180deg, 
                        rgba(235,210,140,0.25) 0%, 
                        rgba(225,200,130,0.06) 45%,
                        rgba(235,210,140,${nodeGlow(VERTEBRAE[i + 1]) > 0.2 ? 0.18 : 0.02}) 100%)`,
                      filter: 'blur(3px)',
                      borderRadius: 4,
                    }} />
                  </motion.div>
                )}

                {/* RELEASE — soft dissolve at dwelling node */}
                {near && glow > 0.5 && (
                  <>
                    {[0, 1].map(j => (
                      <motion.div
                        key={j}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: 2.5,
                          height: 2.5,
                          background: 'rgba(255,228,160,0.4)',
                          left: `calc(50% + ${-10 + j * 20}px)`,
                          top: `${spineTop + v.y * spineHeight}%`,
                        }}
                        animate={{
                          x: [0, (-10 + j * 20) * 1.5],
                          y: [0, -5 - j * 3],
                          opacity: [0, 0.3, 0],
                        }}
                        transition={{
                          duration: 3.5 + j * 0.6,
                          repeat: Infinity,
                          delay: j * 0.8,
                          ease: 'easeOut',
                        }}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}

          {/* === 1. FLOWING LIGHT — smooth continuous stream, not a jumping point === */}
          {/* Leading glow — where the light currently is */}
          {lightY > 0.03 && phase !== 'integration' && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                top: `${spineTop + lightY * spineHeight}%`,
                width: 45 + breath * 12 + (baseGrounding * 30),
                height: 35 + breath * 8 + (baseGrounding * 15),
                opacity: intensity * 0.75,
              }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
            >
              <div className="w-full h-full rounded-full" style={{
                background: `radial-gradient(ellipse, 
                  rgba(255,235,165,${0.5 + intensity * 0.25}) 0%, 
                  rgba(245,220,145,${0.18 + intensity * 0.1}) 35%, 
                  transparent 60%)`,
                filter: 'blur(5px)',
              }} />
            </motion.div>
          )}

          {/* Trailing wake — soft glow behind the light */}
          {lightY > 0.08 && phase !== 'integration' && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                top: `${spineTop + Math.max(0.04, lightY - 0.06) * spineHeight}%`,
                width: 25 + breath * 6,
                height: 20 + breath * 4,
                opacity: intensity * 0.35,
              }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            >
              <div className="w-full h-full rounded-full" style={{
                background: 'radial-gradient(ellipse, rgba(245,220,150,0.3) 0%, rgba(235,210,135,0.08) 40%, transparent 60%)',
                filter: 'blur(6px)',
              }} />
            </motion.div>
          )}

          {/* === 3. BASE GROUNDING — soft expansion and dissolve at completion === */}
          {baseGrounding > 0 && phase !== 'integration' && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                top: `${spineTop + 0.9 * spineHeight}%`,
                width: 80 + baseGrounding * 80 + breath * 10,
                height: 50 + baseGrounding * 40 + breath * 6,
                opacity: baseGrounding * 0.4 * intensity,
              }}
              transition={{ duration: 3, ease: 'easeInOut' }}
            >
              <div className="w-full h-full rounded-full" style={{
                background: 'radial-gradient(ellipse, rgba(235,210,140,0.3) 0%, rgba(220,195,125,0.1) 35%, transparent 55%)',
                filter: 'blur(12px)',
              }} />
            </motion.div>
          )}

          {/* CONNECTION LAYER — spinal warmth extends beyond body */}
          {(phase === 'warmth' || phase === 'integration') && (
            <motion.div className="absolute inset-0 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{
                opacity: phase === 'warmth'
                  ? Math.min(0.25, (progress - 0.5) * 0.5) * (0.85 + breath * 0.15)
                  : 0.3 * (0.85 + breath * 0.15),
              }}
              transition={{ duration: 6, ease: 'easeInOut' }}>
              <div className="absolute inset-0" style={{
                background: `radial-gradient(ellipse 80% 90% at 50% 48%, rgba(235,212,145,0.15) 0%, rgba(220,198,125,0.05) 30%, transparent 55%)`,
              }} />
            </motion.div>
          )}

          {/* === INTEGRATION — full spine settled, gentle warmth === */}
          {phase === 'integration' && (
            <motion.div
              className="absolute pointer-events-none"
              style={{
                left: '50%', top: '48%',
                transform: 'translate(-50%, -50%)',
                width: '70vw', height: '82vh',
                background: 'radial-gradient(ellipse 42% 58% at 50% 46%, rgba(235,212,142,0.3) 0%, rgba(220,198,120,0.08) 40%, transparent 58%)',
              }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: Math.min(0.45, (progress - voiceEnd) / (1 - voiceEnd) * 0.45) * (0.75 + breath * 0.25),
              }}
              transition={{ duration: 5 }}
            />
          )}
        </div>
      )}

      {/* Subtle ambient particles */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={`p-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2,
            height: 2,
            background: `rgba(220,195,130,${0.06 + (i % 3) * 0.03})`,
            left: `${22 + (i * 13) % 56}%`,
            top: `${14 + (i * 15) % 68}%`,
          }}
          animate={{
            y: [0, -8 - (i % 3) * 3, 0],
            x: [0, -3 + (i % 3) * 3, 0],
            opacity: [0, 0.1 + intensity * 0.06, 0],
          }}
          transition={{
            duration: 14 + (i % 3) * 3,
            repeat: Infinity,
            delay: i * 2.5,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export default HealingVisualEngine;
